import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import { 
  Sprout, 
  Package, 
  AlertTriangle, 
  CheckCircle2, 
  Plus, 
  RefreshCw, 
  Truck, 
  Gift, 
  Search, 
  ShieldCheck, 
  FileText, 
  Sliders,
  Layers,
  ArrowUpRight,
  Trash2,
  MapPin,
  Pencil,
  Users,
  Eye
} from 'lucide-react';

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

interface AdminSponsorship {
  farm_sponsorship_id: number;
  public_id: string;
  status: string;
  benefit_type: 'produce' | 'rita_credit';
  sponsorship_amount: number;
  currency_code: string;
  capacity_reserved: number;
  created_at: string;
  paid_at: string | null;
  sponsor: { user_id: number; name: string; email: string; phone_number?: string } | null;
  farm_project: {
    farm_project_id: number;
    project?: { title: string; location?: string };
  } | null;
  beneficiaries: Array<{
    full_name: string;
    phone_e164: string;
    verification_status: string;
    allocation_percentage: number | null;
  }>;
}

interface FarmProject {
  farm_project_id: number;
  project: {
    project_id: number;
    title: string;
    location?: string;
    images?: Array<{ image_id: number; image_url: string }>;
  };
  sponsorship_capacity: number;
  reserved_capacity: number;
  is_sponsorship_open: boolean;
  benefit_options: ('produce' | 'rita_credit')[];
  estimated_harvest_at: string | null;
  max_beneficiaries_per_sponsorship?: number;
}

interface HarvestBatch {
  harvest_batch_id: number;
  batch_code: string;
  status: string;
  available_quantity: number;
  quantity_unit: string;
  available_credit_value: number | null;
  currency_code: string | null;
  created_at: string;
}

interface Distribution {
  benefit_distribution_id: number;
  benefit_type: 'produce' | 'rita_credit';
  status: string;
  allocated_quantity: number | null;
  quantity_unit: string | null;
  allocated_credit_amount: number | null;
  currency_code: string | null;
  fulfillment_reference: string | null;
  failure_reason: string | null;
  beneficiary: { full_name: string; phone_e164: string; verification_status: string };
  harvest_batch: { batch_code: string };
  farm_sponsorship?: { public_id: string };
}

interface OperationsData {
  farms: FarmProject[];
  batches: HarvestBatch[];
  distributions: Distribution[];
  sponsorships?: AdminSponsorship[];
  failed_rita_credit_count: number;
}

const FarmOperations: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<OperationsData | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [loading, setLoading] = useState(true);

  // Active Admin View Tab: 'sponsorships' | 'overview' | 'projects' | 'harvest' | 'rita'
  const [activeTab, setActiveTab] = useState<'sponsorships' | 'overview' | 'projects' | 'harvest' | 'rita'>('sponsorships');

  // Modals & Form State
  const [showCreateFarmModal, setShowCreateFarmModal] = useState(false);
  const [showCreateBatchModal, setShowCreateBatchModal] = useState(false);
  const [selectedDistribution, setSelectedDistribution] = useState<Distribution | null>(null);
  const [selectedSponsorship, setSelectedSponsorship] = useState<AdminSponsorship | null>(null);
  const [availableProjects, setAvailableProjects] = useState<Array<{ project_id: number; title: string; location?: string }>>([]);
  const [deletingFarmId, setDeletingFarmId] = useState<number | null>(null);

  // Edit Farm Project Modal State
  const [editingFarm, setEditingFarm] = useState<FarmProject | null>(null);
  const [editCapacity, setEditCapacity] = useState<string>('150');
  const [editHarvestDate, setEditHarvestDate] = useState<string>('');
  const [editBenefitOptions, setEditBenefitOptions] = useState<('produce' | 'rita_credit')[]>(['produce', 'rita_credit']);
  const [editMaxBeneficiaries, setEditMaxBeneficiaries] = useState<string>('5');
  const [editIsOpen, setEditIsOpen] = useState<boolean>(true);

  // Form Inputs: Create Farm
  const [createMode, setCreateMode] = useState<'existing' | 'scratch'>('existing');
  const [projectId, setProjectId] = useState('');
  const [capacity, setCapacity] = useState('100');
  const [benefitOptions, setBenefitOptions] = useState<('produce' | 'rita_credit')[]>(['produce', 'rita_credit']);
  const [estimatedHarvest, setEstimatedHarvest] = useState('');
  const [isOpen, setIsOpen] = useState(true);

  // Scratch Farm Form Inputs
  const [newTitle, setNewTitle] = useState('');
  const [newShortDesc, setNewShortDesc] = useState('');
  const [newLocation, setNewLocation] = useState('Ibadan, Oyo State, Nigeria');
  const [newFundingGoal, setNewFundingGoal] = useState('10000000');

  // Form Inputs: Create Batch
  const [batchFarmId, setBatchFarmId] = useState<number | null>(null);
  const [availableQty, setAvailableQty] = useState('1000');
  const [qtyUnit, setQtyUnit] = useState('kg');
  const [creditVal, setCreditVal] = useState('500000');
  const [creditCurrency, setCreditCurrency] = useState('NGN');

  // Form Inputs: Fulfillment Update
  const [fulfillmentStatus, setFulfillmentStatus] = useState('completed');
  const [fulfillmentRef, setFulfillmentRef] = useState('');

  const token = localStorage.getItem('diasporawise_admin_token') || localStorage.getItem('token');

  const loadData = async () => {
    if (!token) {
      setMessage({ type: 'error', text: 'Admin authentication token missing.' });
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/admin/farm-operations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = (await response.json()) as ApiResponse<OperationsData>;
      if (!payload.success) throw new Error(payload.message);
      setData(payload.data);

      // Fetch Investment Projects List for Farm Configuration Dropdown
      try {
        const projRes = await fetch(`${API_BASE_URL}/admin/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (projRes.ok) {
          const projPayload = await projRes.json();
          if (projPayload.success && Array.isArray(projPayload.data)) {
            setAvailableProjects(projPayload.data);
            if (projPayload.data.length > 0 && !projectId) {
              setProjectId(String(projPayload.data[0].project_id));
            }
          }
        }
      } catch (pErr) {
        console.error('Failed to fetch projects list for farm operations dropdown:', pErr);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Unable to load farm operations data.'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const handleCreateFarm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    try {
      let targetProjectId = Number(projectId);

      // Scratch Mode: Create Base Project with Agriculture Category automatically
      if (createMode === 'scratch') {
        if (!newTitle.trim()) throw new Error('Farm Project Title is required.');
        
        // 1. Fetch categories to auto-apply Agriculture
        let categoryId = 1;
        try {
          const catRes = await fetch(`${API_BASE_URL}/admin/get-all-category`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (catRes.ok) {
            const catPayload = await catRes.json();
            if (catPayload.success && Array.isArray(catPayload.data)) {
              const agriCat = catPayload.data.find((c: any) => c.name.toLowerCase().includes('agri') || c.name.toLowerCase().includes('farm'));
              if (agriCat) categoryId = agriCat.category_id;
              else if (catPayload.data.length > 0) categoryId = catPayload.data[0].category_id;
            }
          }
        } catch (catErr) {
          console.error('Could not fetch categories, using default ID:', catErr);
        }

        // 2. Create Base Project with Agriculture Category & Clean Defaults
        const createProjRes = await fetch(`${API_BASE_URL}/admin/create-project`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            title: newTitle.trim(),
            short_description: newShortDesc.trim() || `${newTitle.trim()} Farm Project for Diaspora Sponsorship.`,
            full_description: `${newTitle.trim()} - Commercial agriculture project in ${newLocation}. Beneficiaries receive doorstep produce delivery or Rita.ng shopping credit.`,
            category_id: categoryId,
            location: newLocation || 'Nigeria',
            latitude: 7.37,
            longitude: 3.94,
            funding_goal: Number(newFundingGoal) || 10000000,
            expected_roi_percentage: 24,
            roi_duration_months: 12,
            currency_code: 'NGN',
            status: 'active',
            rules: {
              auto_close_on_goal: true,
              min_allowed_investment: 100,
              max_allowed_investment: 1000000
            }
          })
        });

        const projPayload = await createProjRes.json();
        if (!projPayload.success || !projPayload.data?.project_id) {
          throw new Error(projPayload.message || 'Failed to create base farm project.');
        }

        targetProjectId = projPayload.data.project_id;
      }

      if (!targetProjectId) throw new Error('Please select or specify a Base Project.');

      // 3. Configure Farm Project Specs
      const response = await fetch(`${API_BASE_URL}/admin/farm-projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          project_id: targetProjectId,
          sponsorship_capacity: Number(capacity),
          benefit_options: benefitOptions,
          estimated_harvest_at: estimatedHarvest ? new Date(estimatedHarvest).toISOString() : null,
          is_sponsorship_open: isOpen
        })
      });
      const payload = (await response.json()) as ApiResponse<unknown>;
      if (!payload.success) throw new Error(payload.message);
      setMessage({ type: 'success', text: 'Farm project successfully created and configured with Agriculture Category!' });
      setShowCreateFarmModal(false);
      setNewTitle('');
      setNewShortDesc('');
      await loadData();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to create farm project.' });
    }
  };

  const [isBatchSubmitting, setIsBatchSubmitting] = useState(false);
  const [batchModalError, setBatchModalError] = useState<string | null>(null);

  const handleCreateBatch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !batchFarmId) return;
    setIsBatchSubmitting(true);
    setBatchModalError(null);

    try {
      // 1. Create Batch
      const batchRes = await fetch(`${API_BASE_URL}/admin/farm-harvest-batches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          farm_project_id: batchFarmId,
          available_quantity: Number(availableQty),
          quantity_unit: qtyUnit.trim() || 'kg',
          available_credit_value: Number(creditVal) || null,
          currency_code: creditCurrency.trim().toUpperCase() || 'NGN',
          status: 'ready'
        })
      });

      const batchPayload = await batchRes.json();
      if (!batchPayload.success || !batchPayload.data?.harvest_batch_id) {
        throw new Error(batchPayload.message || 'Failed to create harvest batch.');
      }

      const batchId = batchPayload.data.harvest_batch_id;

      // 2. Automatically generate distributions across sponsorships
      const distRes = await fetch(
        `${API_BASE_URL}/admin/farm-harvest-batches/${batchId}/distributions`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const distPayload = await distRes.json();
      if (!distPayload.success) {
        throw new Error(distPayload.message || 'Harvest batch created, but failed to generate distributions.');
      }

      const count = distPayload.data?.created ?? 0;
      setMessage({
        type: 'success',
        text: count > 0
          ? `Harvest batch #${batchId} created successfully! Generated ${count} beneficiary distribution records.`
          : `Harvest batch #${batchId} created successfully! (No active sponsorships awaiting harvest yet).`
      });

      setShowCreateBatchModal(false);
      setActiveTab('overview');
      await loadData();
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Failed to create harvest batch.';
      setBatchModalError(errMsg);
      setMessage({ type: 'error', text: errMsg });
    } finally {
      setIsBatchSubmitting(false);
    }
  };

  const handleUpdateFulfillment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !selectedDistribution) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/farm-distributions/${selectedDistribution.benefit_distribution_id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            status: fulfillmentStatus,
            fulfillment_reference: fulfillmentRef || 'DELIVERED-DIRECT'
          })
        }
      );
      const payload = (await response.json()) as ApiResponse<unknown>;
      if (!payload.success) throw new Error(payload.message);
      setMessage({ type: 'success', text: 'Distribution status & delivery proof updated!' });
      setSelectedDistribution(null);
      await loadData();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Unable to update distribution.' });
    }
  };

  const issueCredit = async (distributionId: number) => {
    if (!token) return;
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/farm-distributions/${distributionId}/rita-credit`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const payload = (await response.json()) as ApiResponse<unknown>;
      if (!payload.success) throw new Error(payload.message);
      setMessage({ type: 'success', text: 'Rita.ng shopping credit issued successfully!' });
      await loadData();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Unable to issue Rita credit.'
      });
    }
  };

  const handleDeleteFarm = async (farmId: number) => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/farm-projects/${farmId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = await res.json();
      if (!payload.success) throw new Error(payload.message);
      setMessage({ type: 'success', text: 'Farm project deleted successfully.' });
      setDeletingFarmId(null);
      await loadData();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to delete farm project.' });
      setDeletingFarmId(null);
    }
  };

  const startEditFarm = (farm: FarmProject) => {
    setEditingFarm(farm);
    setEditCapacity(String(farm.sponsorship_capacity));
    setEditHarvestDate(farm.estimated_harvest_at ? farm.estimated_harvest_at.split('T')[0] : '');
    setEditBenefitOptions(farm.benefit_options || ['produce', 'rita_credit']);
    setEditMaxBeneficiaries(String(farm.max_beneficiaries_per_sponsorship || 5));
    setEditIsOpen(farm.is_sponsorship_open);
  };

  const handleUpdateFarm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !editingFarm) return;
    try {
      const res = await fetch(`${API_BASE_URL}/admin/farm-projects/${editingFarm.farm_project_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          sponsorship_capacity: Number(editCapacity),
          estimated_harvest_at: editHarvestDate ? new Date(editHarvestDate).toISOString() : null,
          benefit_options: editBenefitOptions,
          max_beneficiaries_per_sponsorship: Number(editMaxBeneficiaries),
          is_sponsorship_open: editIsOpen
        })
      });
      const payload = await res.json();
      if (!payload.success) throw new Error(payload.message);
      setMessage({ type: 'success', text: 'Farm project updated successfully!' });
      setEditingFarm(null);
      await loadData();
    } catch (err) {
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to update farm project.' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-3">
          <Sprout className="w-10 h-10 text-emerald-600 animate-spin mx-auto" />
          <p className="text-gray-600 font-bold text-sm">Loading Farm Operations Console...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 p-6 space-y-8">
      {/* Header & Quick Action */}
      <header className="flex flex-wrap justify-between items-center gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold mb-1">
            <ShieldCheck className="w-3.5 h-3.5" /> Operations Console
          </div>
          <h1 className="text-2xl font-extrabold text-gray-900">Farm Project Operations</h1>
          <p className="text-gray-500 text-xs mt-0.5">
            Manage farm project capacity, trigger harvest batches, fulfillment, and Rita.ng credit issuance.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => void loadData()}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button
            onClick={() => navigate('/admin/create-farm-project')}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-sm transition-all flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> Create & Configure Farm Project
          </button>
        </div>
      </header>

      {/* Global Notification Banner */}
      {message && (
        <div
          className={`p-4 rounded-2xl flex items-center justify-between shadow-sm border ${
            message.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}
        >
          <p className="text-xs font-bold">{message.text}</p>
          <button onClick={() => setMessage(null)} className="text-xs underline font-semibold">
            Dismiss
          </button>
        </div>
      )}

      {/* Admin KPI Overview Cards */}
      <section className="grid sm:grid-cols-5 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-purple-100 text-purple-700 rounded-xl">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Sponsorships</p>
            <h3 className="text-xl font-black text-gray-900">{data?.sponsorships?.length ?? 0}</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-emerald-100 text-emerald-700 rounded-xl">
            <Sprout className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Farm Projects</p>
            <h3 className="text-xl font-black text-gray-900">{data?.farms.length ?? 0}</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-amber-100 text-amber-700 rounded-xl">
            <Package className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Harvest Batches</p>
            <h3 className="text-xl font-black text-gray-900">{data?.batches.length ?? 0}</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-blue-100 text-blue-700 rounded-xl">
            <Truck className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Distributions</p>
            <h3 className="text-xl font-black text-gray-900">{data?.distributions.length ?? 0}</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-rose-100 text-rose-700 rounded-xl">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div>
            <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Failed Rita</p>
            <h3 className="text-xl font-black text-gray-900">{data?.failed_rita_credit_count ?? 0}</h3>
          </div>
        </div>
      </section>

      {/* Operations Tab Navigation */}
      <div className="flex border-b border-gray-200 overflow-x-auto gap-4">
        {[
          { key: 'sponsorships', label: 'Farm Sponsorships & Sponsors', icon: Users },
          { key: 'overview', label: 'All Distributions', icon: Layers },
          { key: 'projects', label: 'Farm Projects', icon: Sprout },
          { key: 'harvest', label: 'Harvest Batches', icon: Package },
          { key: 'rita', label: 'Rita.ng Credits', icon: Gift }
        ].map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key as any)}
              className={`pb-3 pt-1 px-4 font-bold text-xs flex items-center gap-2 border-b-2 transition-all flex-shrink-0 ${
                activeTab === t.key
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* TAB 0: FARM SPONSORSHIPS & SPONSORS */}
      {activeTab === 'sponsorships' && (
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center">
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Active Farm Sponsorships & Sponsors</h3>
              <p className="text-[11px] text-gray-400 mt-0.5">
                List of sponsors, funded farm projects, payment amounts, and attached beneficiaries.
              </p>
            </div>
            <span className="text-xs text-gray-500 font-bold">
              Showing total {data?.sponsorships?.length ?? 0} sponsorships
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-gray-50/80 text-gray-600 font-bold uppercase border-b border-gray-100 tracking-wider">
                <tr>
                  <th className="p-4">Sponsor Info</th>
                  <th className="p-4">Farm Project</th>
                  <th className="p-4">Amount Paid</th>
                  <th className="p-4">Benefit Type</th>
                  <th className="p-4">Status</th>
                  <th className="p-4">Beneficiaries</th>
                  <th className="p-4">Date Paid</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-800">
                {(data?.sponsorships ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-10 text-center text-gray-400 font-semibold">
                      <Users className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                      No active farm sponsorships yet.
                    </td>
                  </tr>
                ) : (
                  (data?.sponsorships ?? []).map((s) => (
                    <tr key={s.farm_sponsorship_id || s.public_id} className="hover:bg-gray-50/50">
                      {/* Sponsor Info */}
                      <td className="p-4">
                        <p className="font-extrabold text-gray-900">{s.sponsor?.name || 'Sponsor User'}</p>
                        <p className="text-[11px] text-emerald-700 font-semibold">{s.sponsor?.email || '—'}</p>
                        {s.sponsor?.phone_number && (
                          <p className="text-[10px] text-gray-400">{s.sponsor.phone_number}</p>
                        )}
                      </td>

                      {/* Farm Project */}
                      <td className="p-4">
                        <p className="font-extrabold text-gray-900">{s.farm_project?.project?.title || 'Farm Project'}</p>
                        {s.farm_project?.project?.location && (
                          <p className="text-[10px] text-gray-500 flex items-center gap-0.5">
                            <MapPin className="w-2.5 h-2.5 text-emerald-600" />
                            {s.farm_project.project.location}
                          </p>
                        )}
                      </td>

                      {/* Amount Paid */}
                      <td className="p-4 font-black text-gray-900 text-sm">
                        {s.sponsorship_amount} {s.currency_code}
                      </td>

                      {/* Benefit Type */}
                      <td className="p-4">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase ${
                            s.benefit_type === 'produce'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}
                        >
                          {s.benefit_type === 'produce' ? '🌿 Produce' : '🛒 Rita Credit'}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="p-4">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase ${
                            s.status === 'completed'
                              ? 'bg-emerald-100 text-emerald-800'
                              : s.status === 'awaiting_harvest'
                              ? 'bg-blue-100 text-blue-800'
                              : s.status === 'distributing'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {s.status}
                        </span>
                      </td>

                      {/* Beneficiaries Breakdown */}
                      <td className="p-4">
                        <div className="space-y-1 max-w-xs">
                          <p className="text-[10px] font-bold text-gray-500 uppercase">
                            Total: {s.beneficiaries?.length || 0} beneficiary(s)
                          </p>
                          {(s.beneficiaries || []).map((b, idx) => (
                            <div key={idx} className="text-[11px] bg-gray-100/70 px-2 py-1 rounded border border-gray-200/50 flex justify-between gap-2">
                              <span className="font-semibold text-gray-800 truncate">{b.full_name}</span>
                              <span className="font-bold text-emerald-700">{b.allocation_percentage}%</span>
                            </div>
                          ))}
                        </div>
                      </td>

                      {/* Date Paid */}
                      <td className="p-4">
                        <p className="font-semibold text-gray-800 text-[11px]">
                          {s.paid_at ? new Date(s.paid_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : new Date(s.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </p>
                        <p className="text-[10px] text-gray-400 font-mono">ID: {s.public_id.slice(0, 8)}...</p>
                      </td>

                      {/* Actions */}
                      <td className="p-4 text-right">
                        <button
                          onClick={() => setSelectedSponsorship(s)}
                          className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ml-auto border border-emerald-200/60 shadow-2xs"
                        >
                          <Eye className="w-3.5 h-3.5" /> View Details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* TAB 1: ALL BENEFICIARY DISTRIBUTIONS */}
      {activeTab === 'overview' && (
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center">
            <h3 className="font-bold text-gray-900 text-sm">Beneficiary Distribution Queue</h3>
            <span className="text-xs text-gray-500 font-bold">
              Showing total {data?.distributions.length ?? 0} distributions
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-gray-50/80 text-gray-600 font-bold uppercase border-b border-gray-100">
                <tr>
                  <th className="p-3.5">ID / Batch</th>
                  <th className="p-3.5">Beneficiary</th>
                  <th className="p-3.5">Type & Entitlement</th>
                  <th className="p-3.5">Status</th>
                  <th className="p-3.5">Fulfillment Proof</th>
                  <th className="p-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-800">
                {(data?.distributions ?? []).map((dist) => (
                  <tr key={dist.benefit_distribution_id} className="hover:bg-gray-50/50">
                    <td className="p-3.5">
                      <span className="font-bold">#{dist.benefit_distribution_id}</span>
                      <p className="text-[10px] font-mono text-gray-400">{dist.harvest_batch?.batch_code}</p>
                    </td>

                    <td className="p-3.5">
                      <p className="font-bold text-gray-900">{dist.beneficiary.full_name}</p>
                      <p className="text-[10px] text-gray-500">{dist.beneficiary.phone_e164}</p>
                    </td>

                    <td className="p-3.5 font-bold">
                      {dist.benefit_type === 'rita_credit' ? (
                        <span className="text-purple-700">
                          {dist.allocated_credit_amount} {dist.currency_code} (Rita Credit)
                        </span>
                      ) : (
                        <span className="text-emerald-700">
                          {dist.allocated_quantity} {dist.quantity_unit} (Farm Produce)
                        </span>
                      )}
                    </td>

                    <td className="p-3.5">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${
                          dist.status === 'completed'
                            ? 'bg-emerald-100 text-emerald-800'
                            : dist.status === 'ready'
                            ? 'bg-blue-100 text-blue-800'
                            : dist.status === 'failed'
                            ? 'bg-rose-100 text-rose-800'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {dist.status}
                      </span>
                    </td>

                    <td className="p-3.5 text-gray-500 font-mono text-[10px]">
                      {dist.fulfillment_reference || '—'}
                    </td>

                    <td className="p-3.5 text-right space-x-2">
                      {dist.benefit_type === 'produce' && dist.status !== 'completed' && (
                        <button
                          onClick={() => setSelectedDistribution(dist)}
                          className="px-2.5 py-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-[10px] font-bold"
                        >
                          Update Delivery
                        </button>
                      )}

                      {dist.benefit_type === 'rita_credit' && dist.status !== 'completed' && (
                        <button
                          disabled={dist.beneficiary.verification_status !== 'verified'}
                          onClick={() => void issueCredit(dist.benefit_distribution_id)}
                          className="px-2.5 py-1 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-[10px] font-bold disabled:opacity-50"
                        >
                          Issue Credit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* TAB 2: FARM PROJECTS MANAGEMENT */}
      {activeTab === 'projects' && (
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {/* Table Header */}
          <div className="p-4 border-b border-gray-100 flex justify-between items-center">
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Configured Farm Projects</h3>
              <p className="text-[11px] text-gray-400 mt-0.5">{data?.farms.length ?? 0} farm project(s) active</p>
            </div>
            <button
              onClick={() => navigate('/admin/create-farm-project')}
              className="px-3.5 py-1.5 bg-emerald-600 text-white font-bold text-xs rounded-xl flex items-center gap-1 hover:bg-emerald-700 transition-all"
            >
              <Plus className="w-3.5 h-3.5" /> Add Farm Project
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-gray-50/80 text-gray-500 font-extrabold uppercase border-b border-gray-100 tracking-wide">
                <tr>
                  <th className="p-4 w-14">Image</th>
                  <th className="p-4">Project</th>
                  <th className="p-4">Capacity</th>
                  <th className="p-4">Harvest Date</th>
                  <th className="p-4">Benefit Options</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(data?.farms ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-10 text-center text-gray-400 font-semibold">
                      <Sprout className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                      No farm projects configured yet.
                    </td>
                  </tr>
                ) : (
                  (data?.farms ?? []).map((farm) => {
                    const thumb = farm.project?.images?.[0]?.image_url;
                    const usedPct = farm.sponsorship_capacity > 0
                      ? Math.round((farm.reserved_capacity / farm.sponsorship_capacity) * 100)
                      : 0;
                    const isConfirmingDelete = deletingFarmId === farm.farm_project_id;

                    return (
                      <tr key={farm.farm_project_id} className="hover:bg-gray-50/60 transition-colors">
                        {/* Image Thumbnail */}
                        <td className="p-3">
                          {thumb ? (
                            <img
                              src={thumb}
                              alt={farm.project?.title}
                              className="w-12 h-12 rounded-xl object-cover border border-gray-100 shadow-sm"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                              <Sprout className="w-5 h-5 text-emerald-400" />
                            </div>
                          )}
                        </td>

                        {/* Project Info */}
                        <td className="p-4">
                          <p className="font-extrabold text-gray-900 text-sm leading-tight">{farm.project?.title || 'Farm Project'}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">ID #{farm.farm_project_id} · Project #{farm.project?.project_id}</p>
                          {farm.project?.location && (
                            <p className="text-[10px] text-emerald-600 font-semibold mt-0.5 flex items-center gap-0.5">
                              <MapPin className="w-2.5 h-2.5" />{farm.project.location}
                            </p>
                          )}
                        </td>

                        {/* Capacity */}
                        <td className="p-4">
                          <p className="font-bold text-gray-900">
                            {farm.reserved_capacity} <span className="text-gray-400 font-medium">/ {farm.sponsorship_capacity}</span>
                          </p>
                          <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full w-28 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                usedPct >= 90 ? 'bg-rose-500' : usedPct >= 60 ? 'bg-amber-400' : 'bg-emerald-500'
                              }`}
                              style={{ width: `${Math.min(usedPct, 100)}%` }}
                            />
                          </div>
                          <p className="text-[10px] text-gray-400 mt-0.5">{usedPct}% filled</p>
                        </td>

                        {/* Harvest Date */}
                        <td className="p-4">
                          {farm.estimated_harvest_at ? (
                            <>
                              <p className="font-semibold text-gray-800">
                                {new Date(farm.estimated_harvest_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </p>
                              <p className="text-[10px] text-gray-400">Est. harvest</p>
                            </>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>

                        {/* Benefit Options */}
                        <td className="p-4">
                          <div className="flex flex-wrap gap-1">
                            {farm.benefit_options.map((opt) => (
                              <span
                                key={opt}
                                className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${
                                  opt === 'produce'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : 'bg-purple-100 text-purple-800'
                                }`}
                              >
                                {opt === 'produce' ? '🌿 Produce' : '🛒 Rita Credit'}
                              </span>
                            ))}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="p-4">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-extrabold ${
                              farm.is_sponsorship_open
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-gray-100 text-gray-600'
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${farm.is_sponsorship_open ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                            {farm.is_sponsorship_open ? 'OPEN' : 'CLOSED'}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2 flex-wrap">
                            {/* Trigger Harvest */}
                            <button
                              onClick={() => {
                                setBatchFarmId(farm.farm_project_id);
                                setShowCreateBatchModal(true);
                              }}
                              title="Trigger Harvest Batch"
                              className="px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-lg text-[10px] font-bold flex items-center gap-1 transition-all"
                            >
                              <Package className="w-3.5 h-3.5" /> Harvest
                            </button>

                            {/* Edit */}
                            <button
                              onClick={() => navigate(`/admin/edit-farm-project/${farm.farm_project_id}`)}
                              title="Edit Farm Project Full Page"
                              className="px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-[10px] font-bold flex items-center gap-1 transition-all"
                            >
                              <Pencil className="w-3.5 h-3.5" /> Edit
                            </button>

                            {/* Delete */}
                            {!isConfirmingDelete ? (
                              <button
                                onClick={() => setDeletingFarmId(farm.farm_project_id)}
                                title="Delete Farm Project"
                                className="px-2.5 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg text-[10px] font-bold flex items-center gap-1 transition-all"
                              >
                                <Trash2 className="w-3.5 h-3.5" /> Delete
                              </button>
                            ) : (
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] font-bold text-rose-700">Confirm?</span>
                                <button
                                  onClick={() => void handleDeleteFarm(farm.farm_project_id)}
                                  className="px-2 py-1 bg-rose-600 text-white rounded-lg text-[10px] font-bold hover:bg-rose-700"
                                >
                                  Yes
                                </button>
                                <button
                                  onClick={() => setDeletingFarmId(null)}
                                  className="px-2 py-1 bg-gray-100 text-gray-700 rounded-lg text-[10px] font-bold hover:bg-gray-200"
                                >
                                  No
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* TAB 3: HARVEST BATCHES */}
      {activeTab === 'harvest' && (
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex justify-between items-center">
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Recorded Harvest Batches</h3>
              <p className="text-[11px] text-gray-400 mt-0.5">
                List of triggered harvest yields, available quantities, and shopping credit valuations.
              </p>
            </div>
            <span className="text-xs text-gray-500 font-bold">
              Showing total {data?.batches?.length ?? 0} batch(es)
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-gray-50/80 text-gray-600 font-bold uppercase border-b border-gray-100 tracking-wider">
                <tr>
                  <th className="p-4">Batch Code & ID</th>
                  <th className="p-4">Harvest Yield (Qty)</th>
                  <th className="p-4">Rita Credit Value</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Created Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-800">
                {(data?.batches ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-10 text-center text-gray-400 font-semibold">
                      <Package className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                      No harvest batches recorded yet. Trigger a harvest from the Farm Projects tab.
                    </td>
                  </tr>
                ) : (
                  (data?.batches ?? []).map((batch) => (
                    <tr key={batch.harvest_batch_id} className="hover:bg-gray-50/50">
                      <td className="p-4">
                        <p className="font-bold text-gray-900">#{batch.harvest_batch_id}</p>
                        <p className="text-[10px] font-mono text-emerald-700">{batch.batch_code}</p>
                      </td>
                      <td className="p-4 font-extrabold text-emerald-700">
                        {batch.available_quantity} {batch.quantity_unit}
                      </td>
                      <td className="p-4 font-extrabold text-purple-700">
                        {batch.available_credit_value ? `${batch.available_credit_value} ${batch.currency_code || 'NGN'}` : '—'}
                      </td>
                      <td className="p-4">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase ${
                            batch.status === 'completed'
                              ? 'bg-emerald-100 text-emerald-800'
                              : batch.status === 'distributing'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {batch.status}
                        </span>
                      </td>
                      <td className="p-4 text-right font-semibold text-gray-600">
                        {new Date(batch.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* TAB 4: RITA.NG CREDITS */}
      {activeTab === 'rita' && (
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden p-6 space-y-6">
          <div className="flex justify-between items-center border-b border-gray-100 pb-4">
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Rita.ng Shopping Credit Distributions</h3>
              <p className="text-[11px] text-gray-400 mt-0.5">
                Audit Rita.ng voucher credit issuance status and automated retry logs.
              </p>
            </div>
            <span className="px-3 py-1 bg-purple-100 text-purple-800 text-xs font-bold rounded-full">
              Failed Issuances: {data?.failed_rita_credit_count ?? 0}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-gray-50/80 text-gray-600 font-bold uppercase border-b border-gray-100 tracking-wider">
                <tr>
                  <th className="p-4">ID</th>
                  <th className="p-4">Beneficiary</th>
                  <th className="p-4">Rita Credit Amount</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-800">
                {(data?.distributions ?? []).filter(d => d.benefit_type === 'rita_credit').length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-10 text-center text-gray-400 font-semibold">
                      <Gift className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                      No Rita.ng credit distributions recorded yet.
                    </td>
                  </tr>
                ) : (
                  (data?.distributions ?? []).filter(d => d.benefit_type === 'rita_credit').map((dist) => (
                    <tr key={dist.benefit_distribution_id} className="hover:bg-gray-50/50">
                      <td className="p-4 font-bold">#{dist.benefit_distribution_id}</td>
                      <td className="p-4">
                        <p className="font-bold text-gray-900">{dist.beneficiary.full_name}</p>
                        <p className="text-[10px] text-gray-500">{dist.beneficiary.phone_e164}</p>
                      </td>
                      <td className="p-4 font-extrabold text-purple-700">
                        {dist.allocated_credit_amount} {dist.currency_code || 'NGN'}
                      </td>
                      <td className="p-4">
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase ${
                          dist.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : 'bg-purple-100 text-purple-800'
                        }`}>
                          {dist.status}
                        </span>
                      </td>
                      <td className="p-4 text-right">
                        {dist.status !== 'completed' && (
                          <button
                            onClick={() => void issueCredit(dist.benefit_distribution_id)}
                            className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold shadow-xs transition-all"
                          >
                            Issue / Retry Credit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* MODAL 1: CREATE FARM PROJECT */}
      {showCreateFarmModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-5 shadow-2xl overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <h3 className="text-xl font-bold text-gray-900">Create / Configure Farm Project</h3>
              <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 text-[10px] font-extrabold rounded-full uppercase">
                Category: Agriculture (Auto-Applied)
              </span>
            </div>

            {/* Mode Selection Tabs */}
            <div className="grid grid-cols-2 gap-2 bg-gray-100 p-1 rounded-xl text-xs font-bold">
              <button
                type="button"
                onClick={() => setCreateMode('existing')}
                className={`py-2 rounded-lg transition-all ${createMode === 'existing' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500'}`}
              >
                Choose Existing Project
              </button>
              <button
                type="button"
                onClick={() => setCreateMode('scratch')}
                className={`py-2 rounded-lg transition-all ${createMode === 'scratch' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500'}`}
              >
                + New Farm Project (1-Click)
              </button>
            </div>

            <form onSubmit={handleCreateFarm} className="space-y-4 text-xs font-bold">
              {createMode === 'existing' ? (
                <div>
                  <label className="block text-gray-700 mb-1">Select Investment Project:</label>
                  {availableProjects.length > 0 ? (
                    <select
                      required
                      value={projectId}
                      onChange={(e) => setProjectId(e.target.value)}
                      className="w-full p-2.5 border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-emerald-500 font-semibold text-gray-800"
                    >
                      <option value="">-- Choose Investment Project --</option>
                      {availableProjects.map((proj) => (
                        <option key={proj.project_id} value={proj.project_id}>
                          {proj.title} (#{proj.project_id}) {proj.location ? `- ${proj.location}` : ''}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="number"
                      required
                      value={projectId}
                      onChange={(e) => setProjectId(e.target.value)}
                      placeholder="Enter Project ID (e.g. 1)"
                      className="w-full p-2.5 border border-gray-200 rounded-xl"
                    />
                  )}
                </div>
              ) : (
                <div className="space-y-3 bg-emerald-50/40 p-4 rounded-xl border border-emerald-100">
                  <div>
                    <label className="block text-gray-700 mb-1">Farm Project Title *:</label>
                    <input
                      type="text"
                      required
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      placeholder="e.g. Ogun Greenhouse Tomato & Pepper Farm"
                      className="w-full p-2.5 border border-gray-200 rounded-xl bg-white"
                    />
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-1">Short Description:</label>
                    <input
                      type="text"
                      value={newShortDesc}
                      onChange={(e) => setNewShortDesc(e.target.value)}
                      placeholder="e.g. Commercial tomato drip-irrigation farm cluster in Ogun State."
                      className="w-full p-2.5 border border-gray-200 rounded-xl bg-white"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-gray-700 mb-1">Location:</label>
                      <input
                        type="text"
                        value={newLocation}
                        onChange={(e) => setNewLocation(e.target.value)}
                        placeholder="e.g. Abeokuta, Ogun State"
                        className="w-full p-2.5 border border-gray-200 rounded-xl bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-1">Funding Goal (₦):</label>
                      <input
                        type="number"
                        value={newFundingGoal}
                        onChange={(e) => setNewFundingGoal(e.target.value)}
                        className="w-full p-2.5 border border-gray-200 rounded-xl bg-white"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-gray-700 mb-1">Sponsorship Limit (Capacity Units):</label>
                <input
                  type="number"
                  required
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                  placeholder="e.g. 100 Units"
                  className="w-full p-2.5 border border-gray-200 rounded-xl"
                />
              </div>

              <div>
                <label className="block text-gray-700 mb-1">Estimated Harvest Date:</label>
                <input
                  type="date"
                  value={estimatedHarvest}
                  onChange={(e) => setEstimatedHarvest(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 rounded-xl"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateFarmModal(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl"
                >
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2 bg-emerald-600 text-white rounded-xl">
                  Save Farm Configuration
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: CREATE HARVEST BATCH */}
      {showCreateBatchModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-5 shadow-2xl">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Trigger Harvest Batch</h3>
                <p className="text-xs text-emerald-600 font-bold mt-0.5">
                  {data?.farms.find((f) => f.farm_project_id === batchFarmId)?.project?.title || `Farm Project #${batchFarmId}`}
                </p>
              </div>
              <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full">
                Farm ID #{batchFarmId}
              </span>
            </div>

            {batchModalError && (
              <div className="p-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs font-bold rounded-xl flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0" />
                <span>{batchModalError}</span>
              </div>
            )}

            <form onSubmit={handleCreateBatch} className="space-y-4 text-xs font-bold">
              <div>
                <label className="block text-gray-700 mb-1">Produce Yield Quantity *:</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    required
                    min={1}
                    value={availableQty}
                    onChange={(e) => setAvailableQty(e.target.value)}
                    placeholder="e.g. 1000"
                    className="w-full p-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500"
                  />
                  <input
                    type="text"
                    required
                    value={qtyUnit}
                    onChange={(e) => setQtyUnit(e.target.value)}
                    placeholder="kg / bags"
                    className="w-24 p-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 mb-1">Rita.ng Credit Equivalent Pool:</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={creditVal}
                    onChange={(e) => setCreditVal(e.target.value)}
                    placeholder="e.g. 500000"
                    className="w-full p-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-amber-500"
                  />
                  <input
                    type="text"
                    value={creditCurrency}
                    onChange={(e) => setCreditCurrency(e.target.value)}
                    placeholder="NGN"
                    className="w-24 p-2.5 border border-gray-200 rounded-xl uppercase focus:ring-2 focus:ring-amber-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  disabled={isBatchSubmitting}
                  onClick={() => {
                    setBatchModalError(null);
                    setShowCreateBatchModal(false);
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isBatchSubmitting}
                  className="px-5 py-2 bg-amber-600 hover:bg-amber-700 text-white font-extrabold rounded-xl shadow-md transition-all flex items-center gap-2 disabled:opacity-50"
                >
                  {isBatchSubmitting ? (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Generating Distributions...
                    </>
                  ) : (
                    'Generate Distributions'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: UPDATE DELIVERY PROOF */}
      {selectedDistribution && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-6 shadow-2xl">
            <h3 className="text-xl font-bold text-gray-900">Update Delivery Proof</h3>
            <p className="text-xs text-gray-500">
              Beneficiary: <strong>{selectedDistribution.beneficiary.full_name}</strong>
            </p>
            <form onSubmit={handleUpdateFulfillment} className="space-y-4 text-xs font-bold">
              <div>
                <label className="block text-gray-700 mb-1">Fulfillment Status:</label>
                <select
                  value={fulfillmentStatus}
                  onChange={(e) => setFulfillmentStatus(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 rounded-xl bg-white"
                >
                  <option value="ready">Ready</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 mb-1">Delivery / Waybill Reference:</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. WAYBILL-LAGOS-88231"
                  value={fulfillmentRef}
                  onChange={(e) => setFulfillmentRef(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 rounded-xl"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setSelectedDistribution(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl"
                >
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2 bg-emerald-600 text-white rounded-xl">
                  Update Distribution
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 4: EDIT FARM PROJECT SETTINGS */}
      {editingFarm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-5 shadow-2xl">
            <div className="flex justify-between items-center border-b border-gray-100 pb-3">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Edit Farm Project Parameters</h3>
                <p className="text-xs text-emerald-600 font-semibold">{editingFarm.project?.title}</p>
              </div>
              <span className="text-[10px] font-bold text-gray-400">ID #{editingFarm.farm_project_id}</span>
            </div>

            <form onSubmit={handleUpdateFarm} className="space-y-4 text-xs font-bold">
              <div>
                <label className="block text-gray-700 mb-1">Sponsorship Limit (Capacity Units) *:</label>
                <input
                  type="number"
                  required
                  value={editCapacity}
                  onChange={(e) => setEditCapacity(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 rounded-xl"
                />
              </div>

              <div>
                <label className="block text-gray-700 mb-1">Estimated Harvest Date:</label>
                <input
                  type="date"
                  value={editHarvestDate}
                  onChange={(e) => setEditHarvestDate(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 rounded-xl"
                />
              </div>

              <div>
                <label className="block text-gray-700 mb-1">Max Beneficiaries per Sponsorship:</label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={editMaxBeneficiaries}
                  onChange={(e) => setEditMaxBeneficiaries(e.target.value)}
                  className="w-full p-2.5 border border-gray-200 rounded-xl"
                />
              </div>

              <div>
                <label className="block text-gray-700 mb-1">Allowed Beneficiary Benefit Options:</label>
                <div className="flex gap-3 pt-1">
                  <label className="flex items-center gap-2 cursor-pointer font-bold text-xs">
                    <input
                      type="checkbox"
                      checked={editBenefitOptions.includes('produce')}
                      onChange={(e) => {
                        if (e.target.checked) setEditBenefitOptions([...editBenefitOptions, 'produce']);
                        else if (editBenefitOptions.length > 1) setEditBenefitOptions(editBenefitOptions.filter((o) => o !== 'produce'));
                      }}
                      className="rounded text-emerald-600 focus:ring-emerald-500"
                    />
                    Produce Delivery
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer font-bold text-xs">
                    <input
                      type="checkbox"
                      checked={editBenefitOptions.includes('rita_credit')}
                      onChange={(e) => {
                        if (e.target.checked) setEditBenefitOptions([...editBenefitOptions, 'rita_credit']);
                        else if (editBenefitOptions.length > 1) setEditBenefitOptions(editBenefitOptions.filter((o) => o !== 'rita_credit'));
                      }}
                      className="rounded text-emerald-600 focus:ring-emerald-500"
                    />
                    Rita.ng Credit
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 mb-1">Sponsorship Status:</label>
                <select
                  value={editIsOpen ? 'open' : 'closed'}
                  onChange={(e) => setEditIsOpen(e.target.value === 'open')}
                  className="w-full p-2.5 border border-gray-200 rounded-xl bg-white"
                >
                  <option value="open">OPEN for Sponsorship</option>
                  <option value="closed">CLOSED</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setEditingFarm(null)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2 bg-emerald-600 text-white rounded-xl font-extrabold hover:bg-emerald-700">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* SPONSORSHIP DETAILS MODAL */}
      {selectedSponsorship && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 space-y-6 shadow-2xl border border-gray-100 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-start border-b border-gray-100 pb-4">
              <div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold mb-1">
                  <ShieldCheck className="w-3.5 h-3.5" /> Sponsorship Record Details
                </div>
                <h3 className="text-xl font-extrabold text-gray-900">
                  {selectedSponsorship.farm_project?.project?.title || 'Farm Project Sponsorship'}
                </h3>
                <p className="text-xs text-gray-500 font-mono mt-0.5">
                  Public ID: {selectedSponsorship.public_id}
                </p>
              </div>
              <button
                onClick={() => setSelectedSponsorship(null)}
                className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 flex items-center justify-center font-bold transition-all text-sm"
              >
                ✕
              </button>
            </div>

            {/* Grid 1: Sponsor & Farm Info */}
            <div className="grid sm:grid-cols-2 gap-4">
              {/* Sponsor Card */}
              <div className="bg-emerald-50/50 p-4 rounded-2xl border border-emerald-100 space-y-2">
                <p className="text-[10px] font-extrabold text-emerald-800 uppercase tracking-wider flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" /> Sponsor User Info
                </p>
                <p className="font-extrabold text-gray-900 text-sm">{selectedSponsorship.sponsor?.name || 'Unknown Sponsor'}</p>
                <p className="text-xs text-emerald-700 font-semibold">{selectedSponsorship.sponsor?.email || 'No email'}</p>
                {selectedSponsorship.sponsor?.phone_number && (
                  <p className="text-xs text-gray-500 font-medium">Phone: {selectedSponsorship.sponsor.phone_number}</p>
                )}
                <p className="text-[10px] text-gray-400">User ID: #{selectedSponsorship.sponsor?.user_id}</p>
              </div>

              {/* Project Card */}
              <div className="bg-amber-50/50 p-4 rounded-2xl border border-amber-100 space-y-2">
                <p className="text-[10px] font-extrabold text-amber-800 uppercase tracking-wider flex items-center gap-1">
                  <Sprout className="w-3.5 h-3.5" /> Farm Project Info
                </p>
                <p className="font-extrabold text-gray-900 text-sm">{selectedSponsorship.farm_project?.project?.title || 'Farm Project'}</p>
                {selectedSponsorship.farm_project?.project?.location && (
                  <p className="text-xs text-gray-600 flex items-center gap-1 font-semibold">
                    <MapPin className="w-3.5 h-3.5 text-amber-700" /> {selectedSponsorship.farm_project.project.location}
                  </p>
                )}
                <p className="text-xs text-gray-500">Reserved Units: <span className="font-bold text-gray-800">{selectedSponsorship.capacity_reserved} unit(s)</span></p>
              </div>
            </div>

            {/* Grid 2: Financial & Payment Info */}
            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100 space-y-3">
              <h4 className="text-xs font-extrabold text-gray-700 uppercase tracking-wider flex items-center gap-1">
                <FileText className="w-3.5 h-3.5 text-gray-500" /> Financial & Benefit Breakdown
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div className="bg-white p-3 rounded-xl border border-gray-100">
                  <p className="text-[10px] text-gray-400 font-bold uppercase">Total Amount</p>
                  <p className="font-black text-gray-900 text-base mt-0.5">{selectedSponsorship.sponsorship_amount} {selectedSponsorship.currency_code}</p>
                </div>

                <div className="bg-white p-3 rounded-xl border border-gray-100">
                  <p className="text-[10px] text-gray-400 font-bold uppercase">Benefit Type</p>
                  <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${
                    selectedSponsorship.benefit_type === 'produce' ? 'bg-emerald-100 text-emerald-800' : 'bg-purple-100 text-purple-800'
                  }`}>
                    {selectedSponsorship.benefit_type === 'produce' ? '🌿 Produce' : '🛒 Rita Credit'}
                  </span>
                </div>

                <div className="bg-white p-3 rounded-xl border border-gray-100">
                  <p className="text-[10px] text-gray-400 font-bold uppercase">Status</p>
                  <span className="inline-block mt-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-extrabold uppercase">
                    {selectedSponsorship.status}
                  </span>
                </div>

                <div className="bg-white p-3 rounded-xl border border-gray-100">
                  <p className="text-[10px] text-gray-400 font-bold uppercase">Paid Date</p>
                  <p className="font-semibold text-gray-800 mt-1">
                    {selectedSponsorship.paid_at ? new Date(selectedSponsorship.paid_at).toLocaleDateString() : new Date(selectedSponsorship.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Grid 3: Beneficiaries List */}
            <div className="space-y-3">
              <h4 className="text-xs font-extrabold text-gray-800 uppercase tracking-wider flex items-center gap-1">
                <Users className="w-3.5 h-3.5 text-emerald-600" /> Beneficiaries ({selectedSponsorship.beneficiaries?.length || 0})
              </h4>
              <div className="grid sm:grid-cols-2 gap-3">
                {(selectedSponsorship.beneficiaries || []).map((b, idx) => (
                  <div key={idx} className="p-3.5 rounded-xl border border-gray-200/70 bg-white space-y-1.5 shadow-xs">
                    <div className="flex justify-between items-start">
                      <p className="font-bold text-gray-900 text-xs">{b.full_name}</p>
                    </div>
                    <p className="text-[11px] text-gray-500 font-mono">{b.phone_e164}</p>
                    <div className="flex justify-between items-center text-[10px] border-t border-gray-100 pt-1.5 font-bold">
                      <span className="text-gray-400 uppercase">Allocation:</span>
                      <span className="text-emerald-700 text-xs">{b.allocation_percentage}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="pt-2 flex justify-end">
              <button
                onClick={() => setSelectedSponsorship(null)}
                className="px-5 py-2 bg-gray-900 hover:bg-gray-800 text-white font-bold text-xs rounded-xl transition-all"
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmOperations;
